/**
 * WeeklyCheckCard
 * Structured card for weekly_check messages rendered inside the AI chat bubble.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface Props {
  metadata: {
    total_spent?: number;
    total_income?: number;
    spend_trend_percent?: number;
    top_category?: string;
    top_category_amount?: number;
    week_range?: string;
    budget_status?: {
      daily_budget?: number;
      days_remaining?: number;
      status?: string;
      pace_percentage?: number;
    };
    context?: {
      insights?: Array<{
        text: string;
        severity?: 'info' | 'warning' | 'critical';
      }>;
    };
    [key: string]: any;
  };
}

const SEVERITY_COLORS: Record<string, string> = {
  info: '#3B82F6',
  warning: '#F59E0B',
  critical: '#EF4444',
};

const SEVERITY_ICONS: Record<string, string> = {
  info: 'information-outline',
  warning: 'alert-outline',
  critical: 'alert-circle-outline',
};

export default function WeeklyCheckCard({ metadata }: Props) {
  const totalSpent = metadata.total_spent || 0;
  const totalIncome = metadata.total_income || 0;
  const trend = metadata.spend_trend_percent;
  const topCategory = metadata.top_category;
  const weekRange = metadata.week_range || 'cette semaine';
  const budget = metadata.budget_status || {};
  const dailyBudget = budget.daily_budget || 0;
  const daysRemaining = budget.days_remaining || 0;
  const insights = metadata.context?.insights || [];

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Ton bilan de la semaine</Text>
        <Text style={styles.headerSubtitle}>{weekRange}</Text>
      </View>

      {/* Stats */}
      <View style={styles.statsContainer}>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Dépensé</Text>
          <View style={styles.statValueRow}>
            <Text style={styles.statValue}>{totalSpent.toLocaleString('fr-FR')} €</Text>
            {trend != null && (
              <Text style={[styles.trendBadge, { color: trend > 0 ? '#EF4444' : '#10B981' }]}>
                {trend > 0 ? '+' : ''}{trend}% vs sem.
              </Text>
            )}
          </View>
        </View>

        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Reçu</Text>
          <Text style={[styles.statValue, { color: '#10B981' }]}>{totalIncome.toLocaleString('fr-FR')} €</Text>
        </View>

        {topCategory && (
          <View style={styles.statRow}>
            <Text style={styles.statLabel}>Top</Text>
            <Text style={styles.statValue}>{topCategory}</Text>
          </View>
        )}
      </View>

      {/* Insights */}
      {insights.length > 0 && (
        <>
          <View style={styles.divider} />
          <View style={styles.insightsList}>
            {insights.map((insight, index) => {
              const severity = insight.severity || 'info';
              const iconColor = SEVERITY_COLORS[severity] || SEVERITY_COLORS.info;
              const iconName = SEVERITY_ICONS[severity] || SEVERITY_ICONS.info;
              return (
                <View key={index} style={styles.insightRow}>
                  <Icon name={iconName} size={16} color={iconColor} style={styles.insightIcon} />
                  <Text style={styles.insightText}>{insight.text}</Text>
                </View>
              );
            })}
          </View>
        </>
      )}

      {/* Footer */}
      <View style={styles.divider} />
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          {daysRemaining}j restants · {dailyBudget.toLocaleString('fr-FR')} €/jour
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: spacing.md,
    marginVertical: spacing.xs,
  },
  header: {
    marginBottom: spacing.sm,
  },
  headerTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  statsContainer: {
    gap: 6,
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  statValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  statValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  trendBadge: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: spacing.sm,
  },
  insightsList: {
    gap: 6,
  },
  insightRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  insightIcon: {
    marginRight: 6,
    marginTop: 2,
  },
  insightText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text,
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
  },
});
